// =============================================================================
// features/configuracoes/comercial/ComercialSettingsContent.tsx — Conteúdo
// real da tela `/configuracoes/comercial` (doc 26 §6.9, F34-S06). Montado só
// depois que ComercialSettingsPage.tsx confirma permissão + flag.
//
// Impacto em campanha ativa (DoD): qualquer save (settings PATCH ou outcomes
// PUT) passa por `requestSave`, que consulta a contagem REAL de campanhas
// `active` (useCampaignsCount — mesmo hook/endpoint de features/sales/campaigns,
// sem número inventado) e força confirmação explícita antes de executar.
// =============================================================================

import type { SalesOutcomeConfig } from '@elemento/shared-schemas';
import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Toast';
import { useCampaignsCount } from '../../../hooks/sales/useCampaigns';
import {
  useReplaceSalesOutcomes,
  useSalesOutcomesCatalog,
  useSalesSettings,
  useUpdateSalesSettings,
} from '../../../hooks/sales/useSalesSettings';

import { AttemptsSection } from './AttemptsSection';
import { CancelamentosSection } from './CancelamentosSection';
import { ContactWindowSection } from './ContactWindowSection';
import { DistributionSection } from './DistributionSection';
import { ImpactConfirmDialog } from './ImpactConfirmDialog';
import { OutcomesSection } from './OutcomesSection';
import { SaveBar } from './SaveBar';
import {
  describeChanges,
  diffDraft,
  draftFromSettings,
  hasErrors,
  isDirty,
  validateDraft,
} from './types';
import type { SettingsDraft } from './types';

interface PendingImpact {
  labels: string[];
  action: () => void;
}

export function ComercialSettingsContent(): React.JSX.Element {
  const { toast } = useToast();

  const { data: settings, isLoading, isError, refetch } = useSalesSettings();
  const outcomesCatalog = useSalesOutcomesCatalog();
  const { total: activeCampaignsCount } = useCampaignsCount({ status: ['active'] });

  const { updateAsync, isPending: isSavingSettings } = useUpdateSalesSettings();
  const { replaceAsync, isPending: isSavingOutcomes } = useReplaceSalesOutcomes();

  const [draft, setDraft] = React.useState<SettingsDraft | null>(null);
  const [pendingImpact, setPendingImpact] = React.useState<PendingImpact | null>(null);

  const effectiveDraft = draft ?? (settings ? draftFromSettings(settings) : null);

  const requestSave = (labels: string[], action: () => void): void => {
    if (labels.length === 0) return;
    if ((activeCampaignsCount ?? 0) > 0) {
      setPendingImpact({ labels, action });
      return;
    }
    action();
  };

  const handleSettingsChange = (updates: Partial<SettingsDraft>): void => {
    if (!effectiveDraft) return;
    setDraft({ ...effectiveDraft, ...updates });
  };

  const errors = effectiveDraft ? validateDraft(effectiveDraft) : {};

  const handleSaveSettings = (): void => {
    if (!settings || !effectiveDraft || hasErrors(errors)) return;
    const patch = diffDraft(settings, effectiveDraft);
    const labels = describeChanges(patch);
    if (labels.length === 0) return;

    requestSave(labels, () => {
      void updateAsync(patch)
        .then(() => {
          setDraft(null);
          toast('Configuração comercial atualizada.', 'success');
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Erro ao salvar configuração.';
          toast(msg, 'danger');
        })
        .finally(() => setPendingImpact(null));
    });
  };

  const handleSaveOutcomes = (next: SalesOutcomeConfig[], changedCount: number): void => {
    if (changedCount === 0) return;
    const labels = [`${changedCount} desfecho${changedCount !== 1 ? 's' : ''} comercial(is)`];

    requestSave(labels, () => {
      void replaceAsync(next)
        .then(() => toast('Desfechos atualizados.', 'success'))
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Erro ao salvar desfechos.';
          toast(msg, 'danger');
        })
        .finally(() => setPendingImpact(null));
    });
  };

  if (isLoading || outcomesCatalog.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-32 rounded-md animate-pulse"
            style={{ background: 'var(--surface-muted)' }}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  if (isError || !settings || !effectiveDraft) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <p className="font-sans text-ink-3" style={{ fontSize: 'var(--text-sm)' }}>
          Erro ao carregar a configuração comercial.
        </p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  const dirty = isDirty(settings, effectiveDraft);

  return (
    <div className="flex flex-col gap-6 pb-24">
      <ContactWindowSection
        draft={effectiveDraft}
        errors={errors}
        isDefault={settings.isDefault}
        onChange={handleSettingsChange}
        disabled={isSavingSettings}
      />
      <AttemptsSection
        draft={effectiveDraft}
        errors={errors}
        isDefault={settings.isDefault}
        onChange={handleSettingsChange}
        disabled={isSavingSettings}
      />
      <DistributionSection
        draft={effectiveDraft}
        errors={errors}
        isDefault={settings.isDefault}
        onChange={handleSettingsChange}
        disabled={isSavingSettings}
      />
      <CancelamentosSection
        draft={effectiveDraft}
        isDefault={settings.isDefault}
        onChange={handleSettingsChange}
        disabled={isSavingSettings}
      />
      <OutcomesSection
        items={outcomesCatalog.items}
        isDefault={outcomesCatalog.isDefault}
        isPending={isSavingOutcomes}
        onSave={handleSaveOutcomes}
      />

      {/* Barra de salvar fixa — só para os campos de sales_settings (PATCH) */}
      {dirty && (
        <SaveBar
          isPending={isSavingSettings}
          hasErrors={hasErrors(errors)}
          onDiscard={() => setDraft(null)}
          onSave={handleSaveSettings}
        />
      )}

      {pendingImpact && (
        <ImpactConfirmDialog
          activeCampaignsCount={activeCampaignsCount ?? 0}
          changedFields={pendingImpact.labels}
          isPending={isSavingSettings || isSavingOutcomes}
          onConfirm={pendingImpact.action}
          onCancel={() => setPendingImpact(null)}
        />
      )}
    </div>
  );
}
